import * as R from "ramda";
import { Action, ActionFunction1 } from "redux-actions";
import { call, put, select } from "redux-saga/effects";

import { loadExportedSCORMPackagesAmount } from "../../../actions/load-amounts";
import { fetchAndResolveScormPackages, FetchScormPackages } from "../../../actions/scormPackages";
import { SagaFunction } from "../../../actions/types";
import {
  modifyObjectDeletedPatch,
  patchPayloadCreator,
} from "../../../components/course-author/helpers/patches/creators";
import { api } from "../../../dependencies";
import { resolveScormPackagePostSuccessResponseValueToScormPackageUIData as mapResponseToUIData } from "../../../parsers/resolve";
import { SagaAction } from "../../../sagas/types";
import * as selectors from "../../../selectors/selectors";
import { IScormPackage } from "../../types/common";

interface IFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<IScormPackage[], Action<IScormPackage[]>>;
}

export const fetchExportedScormPackages: (furtherActions: IFurtherActions) => SagaFunction = fa =>
  function* _fetchExportedScormPackages(_: Action<void>): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const organizationId: string = yield select(selectors.organizationId);
    const [scormPackagesError, scormPackagesResponses]: FetchScormPackages = yield call(
      fetchAndResolveScormPackages,
      accountId,
      organizationId
    );

    if (scormPackagesError || !scormPackagesResponses) {
      yield put(fa.onFailure({ message: "Error fetching scorm packages" }));
      return;
    }

    const { scormPackageIdsResponse, scormPackagesResponse } = scormPackagesResponses;

    const scormPackages = R.zipWith(
      (uuid, datum) => ({
        ...mapResponseToUIData(datum),
        scormPackageId: uuid,
      }),
      scormPackageIdsResponse.data,
      scormPackagesResponse.data
    ).sort((a: IScormPackage, b: IScormPackage) => a.name.localeCompare(b.name));
    yield put(fa.onSuccess(scormPackages));
    yield put(loadExportedSCORMPackagesAmount());
  };

export interface IDeleteScormPackageFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<IScormPackage[], Action<IScormPackage[]>>;
}

export const deleteScormPackage: (fa: IDeleteScormPackageFurtherActions) => SagaFunction = fa =>
  function* _deleteScormPackage(action: Action<string>): SagaAction {
    const accountId = yield select(selectors.accountId);
    const scormPackageId = action.payload;
    const oldScormPackages: IScormPackage[] = yield select(selectors.exportedScormPackages);
    const scormPackagesForDelete: IScormPackage[] = oldScormPackages.filter(sp => sp.scormPackageId === scormPackageId);
    if (scormPackagesForDelete.length === 0) {
      return yield put(
        fa.onFailure({
          message: "SCORM package requested for delete not found.",
        })
      );
    }
    const versionId = scormPackagesForDelete[0].versionId;
    const deletePatch = yield call(modifyObjectDeletedPatch, versionId, accountId);
    const patchPayload = yield call(patchPayloadCreator, versionId, deletePatch);
    const [patchError] = yield call(api.scormPackages.patchScormPackage, accountId, scormPackageId, patchPayload);
    if (patchError) {
      return yield put(
        fa.onFailure({
          message: "Failed to patch SCORM package as deleted.",
        })
      );
    }
    const scormPackages = oldScormPackages.filter(c => c.scormPackageId !== scormPackageId);
    yield put(fa.onSuccess(scormPackages));
    yield put(loadExportedSCORMPackagesAmount());
  };
